import { useEffect, useState } from "react";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AdminHeader } from "@/components/admin/AdminShared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ROLE_LABELS } from "@/lib/helpers";

const ROLES = ["SUPER_ADMIN", "ADMIN_HUMAS", "VERIFIKATOR", "UNIT_KERJA"];
const ROLE_DESC = {
  SUPER_ADMIN: "Akses seluruh sistem",
  ADMIN_HUMAS: "Berita, media, edukasi, FAQ, aspirasi",
  VERIFIKATOR: "Verifikasi & review konten",
  UNIT_KERJA: "Mengirim bahan berita/informasi",
};
const EMPTY = { name: "", username: "", email: "", role: "ADMIN_HUMAS", password: "" };

export default function UsersAdmin() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/users").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm({ ...form, [k]: v });

  const openNew = () => { setForm(EMPTY); setEditId(null); setOpen(true); };
  const openEdit = (u) => { setForm({ ...u, password: "" }); setEditId(u.id); setOpen(true); };

  const save = async () => {
    if (!form.name || !form.username || !form.email) { toast.error("Lengkapi data pengguna"); return; }
    try {
      if (editId) await api.put(`/users/${editId}`, form);
      else await api.post("/users", form);
      toast.success("Pengguna disimpan"); setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gagal menyimpan"); }
  };
  const del = async (id) => {
    try { await api.delete(`/users/${id}`); toast.success("Pengguna dihapus"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal menghapus"); }
  };

  return (
    <div>
      <AdminHeader title="Manajemen Pengguna" description="Kelola pengguna & hak akses berbasis peran (RBAC)."
        action={<Button onClick={openNew} className="bg-brandred hover:bg-brandred-700 text-white" data-testid="add-user-btn"><Plus className="h-4 w-4 mr-1.5" /> Tambah Pengguna</Button>} />

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {ROLES.map((r) => (
          <div key={r} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="font-bold text-navy text-sm">{ROLE_LABELS[r]}</div>
            <p className="text-xs text-slate-500 mt-1">{ROLE_DESC[r]}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="users-table">
        <Table>
          <TableHeader><TableRow className="bg-surface"><TableHead>Nama</TableHead><TableHead>Username</TableHead><TableHead>Email</TableHead><TableHead>Peran</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((u) => (
              <TableRow key={u.id}>
                <TableCell className="font-semibold text-navy">{u.name}</TableCell>
                <TableCell className="text-sm font-mono text-slate-500">{u.username}</TableCell>
                <TableCell className="text-sm text-slate-500">{u.email}</TableCell>
                <TableCell><span className="text-xs font-bold px-2.5 py-1 rounded-full bg-navy/5 text-navy">{ROLE_LABELS[u.role] || u.role}</span></TableCell>
                <TableCell className="text-right">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(u)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => del(u.id)} className="text-brandred"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Edit" : "Tambah"} Pengguna</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Nama Lengkap</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="mt-1.5" data-testid="user-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Username</Label><Input value={form.username} onChange={(e) => set("username", e.target.value)} className="mt-1.5" disabled={!!editId} data-testid="user-username" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5" data-testid="user-email" /></div>
            </div>
            <div><Label>Peran</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger className="mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Password {editId && "(kosongkan bila tidak diubah)"}</Label><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="mt-1.5" placeholder={editId ? "••••••" : "bawaslu123"} data-testid="user-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={save} className="bg-brandred hover:bg-brandred-700 text-white" data-testid="save-user">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
